// Utility functions for determining interaction status and colors

export interface InteractionStatus {
  status: 'overdue' | 'pending' | 'completed' | 'cancel';
  label: string;
  color: {
    background: string;
    text: string;
    border: string;
  };
  cardColors: {
    background: string;
    border: string;
    text: string;
    avatarBg: string;
    avatarText: string;
  };
}

/**
 * Determine interaction status based on database fields:
 * FIXED: Uses 'date_created' and 'done' fields with better logic
 *
 * 1. ✅ COMPLETED (Green): done = true (ONLY checked/ceklis items)
 * 2. ⏳ SCHEDULED/PENDING (Blue): done = null/undefined (any date)
 * 3. 🔴 OVERDUE (Red): done = null/undefined + date_created < today
 * 4. ❌ CANCELLED (Gray): done = false (explicit cancellation)
 *
 * IMPORTANT: ONLY done = true is considered COMPLETED (checked items in Directus)
 * NULL values are treated as PENDING/SCHEDULED, not completed
 */
export function determineInteractionStatus(
  dateCreated: string,
  done?: boolean | null,
  t?: (key: string) => string
): InteractionStatus {
  // Simplified date handling - focus on database reality
  const now = new Date();
  const createdDate = new Date(dateCreated);
  
  // Date-only comparison (ignore time components)
  const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const createdDateOnly = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
  
  // Key logic: past dates only (today is NOT overdue)
  const isDatePassed = createdDateOnly < nowDateOnly;
  
  // Debug logging for troubleshooting (only in development)
  if (import.meta.env.DEV) {
    // Only log first few occurrences to avoid console spam
    if (!window.statusDebugCount) window.statusDebugCount = 0;

    if (window.statusDebugCount < 3) {
      console.log('🔍 Status Debug:', {
        dateCreated,
        done,
        doneType: typeof done,
        isDatePassed,
        nowDateOnly: nowDateOnly.toDateString(),
        createdDateOnly: createdDateOnly.toDateString()
      });
      window.statusDebugCount++;
    }
  }
  
  // PRIMARY RULE: done field takes precedence
  if (done === true) {
    // ✅ COMPLETED: database says done=true (ONLY checked items)
    return {
      status: 'completed',
      label: t ? t('interactions.completed') : 'Completed',
      color: {
        background: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-800 dark:text-green-300',
        border: 'border-green-300 dark:border-green-700'
      },
      cardColors: {
        background: 'bg-green-50 dark:bg-green-950/20',
        border: 'border-green-200 dark:border-green-800',
        text: 'text-green-900 dark:text-green-100',
        avatarBg: 'bg-green-100 dark:bg-green-800',
        avatarText: 'text-green-800 dark:text-green-100'
      }
    };
  }
  
  // FIXED: Only show CANCELLED if explicitly done=false
  if (done === false) {
    // ❌ CANCELLED: explicitly cancelled by user
    return {
      status: 'cancel',
      label: t ? t('interactions.cancelled') : 'Cancelled',
      color: {
        background: 'bg-gray-100 dark:bg-gray-800/50',
        text: 'text-gray-600 dark:text-gray-400',
        border: 'border-input dark:border-gray-600'
      },
      cardColors: {
        background: 'bg-background dark:bg-gray-900/20',
        border: 'border-gray-200 dark:border-gray-700',
        text: 'text-gray-700 dark:text-gray-300',
        avatarBg: 'bg-gray-200 dark:bg-gray-700',
        avatarText: 'text-gray-600 dark:text-gray-300'
      }
    };
  }
  
  // SECONDARY RULE: For null/undefined done, check date
  if (done === null || done === undefined) {
    if (isDatePassed) {
      // 🔴 OVERDUE: no completion status + past date
      return {
        status: 'overdue',
        label: t ? t('interactions.overdue') : 'Overdue',
        color: {
          background: 'bg-red-100 dark:bg-red-900/30',
          text: 'text-red-800 dark:text-red-300',
          border: 'border-red-300 dark:border-red-700'
        },
        cardColors: {
          background: 'bg-red-50 dark:bg-red-950/20',
          border: 'border-red-200 dark:border-red-800',
          text: 'text-red-900 dark:text-red-100',
          avatarBg: 'bg-red-100 dark:bg-red-800',
          avatarText: 'text-red-800 dark:text-red-100'
        }
      };
    } else {
      // ⏳ SCHEDULED/PENDING: no completion status + today/future date
      return {
        status: 'pending',
        label: t ? t('interactions.scheduled') : 'Scheduled', // FIXED: Show as "Scheduled" instead of "Pending"
        color: {
          background: 'bg-primary/10 dark:bg-primary/20',
          text: 'text-primary dark:text-primary',
          border: 'border-primary/30 dark:border-primary/40'
        },
        cardColors: {
          background: 'bg-primary/5 dark:bg-primary/10',
          border: 'border-primary/20 dark:border-primary/30',
          text: 'text-primary dark:text-primary',
          avatarBg: 'bg-primary/10 dark:bg-primary/20',
          avatarText: 'text-primary dark:text-primary'
        }
      };
    }
  }
  
  // FALLBACK: Default to scheduled for unexpected cases  
  return {
    status: 'pending',
    label: t ? t('interactions.scheduled') : 'Scheduled',
    color: {
      background: 'bg-primary/10 dark:bg-primary/20',
      text: 'text-primary dark:text-primary',
      border: 'border-primary/30 dark:border-primary/40'
    },
    cardColors: {
      background: 'bg-primary/5 dark:bg-primary/10',
      border: 'border-primary/20 dark:border-primary/30',
      text: 'text-primary dark:text-primary',
      avatarBg: 'bg-primary/10 dark:bg-primary/20',
      avatarText: 'text-primary dark:text-primary'
    }
  };
}

/**
 * Helper function to format date for display
 */
export function formatInteractionDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Helper function to check if a date is today
 */
export function isToday(dateString: string): boolean {
  const date = new Date(dateString);
  const today = new Date();
  
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
}

/**
 * Helper function to check if a date is in the past (before today)
 */
export function isPastDate(dateString: string): boolean {
  const date = new Date(dateString);
  const today = new Date();
  
  // Remove time component for comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  
  return dateOnly < todayOnly;
}